import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _typeof from "@babel/runtime/helpers/typeof";
var _excluded = ["children", "spacing", "withDividers", "breakpoints", "as"],
  _excluded2 = ["children", "as", "spacing", "withDividers", "breakpoints"],
  _excluded3 = ["children", "hiddenAbove", "hiddenBelow", "allowUnknownProps"];
import "core-js/modules/es.number.constructor.js";
import "core-js/modules/es.array.map.js";
import React, { createContext, useContext, forwardRef } from 'react';
import styled from 'styled-components';
import { getSpacing, createStyleFunction, getPropFilter } from 'utils/ui';
import Box from 'components/Box';
import Divider from 'components/Divider';
import Hidden from 'components/Hidden';
var spacingCompensationProp = createStyleFunction([{
  styleProp: 'compensateSpacing',
  properties: ['marginBottom'],
  getValue: function getValue(value, theme) {
    return "-".concat(getSpacing(value, theme));
  }
}]);
var dividerCompensationProp = createStyleFunction([{
  styleProp: 'compensateSpacing',
  properties: ['marginBottom'],
  getValue: function getValue(value, theme) {
    var spacing = getSpacing(value, theme);
    if (Number(spacing) === 0) {
      return '-1px';
    } else {
      return "calc(-".concat(spacing, " * 2 - 1px)");
    }
  }
}]);
/**
 * Using a pseudo element to compensate for the spacing
 * between items (instead of negative margin) to allow
 * for the wrapper to seamlessly accept any `Box` props.
 * E.g., if a negative margin-top was used instead, it would
 * clash with the `mt` prop.
 */

var Wrapper = styled(Box).withConfig({
  shouldForwardProp: getPropFilter(['compensateSpacing', 'withDividers'])
}).withConfig({
  displayName: "Stack__Wrapper",
  componentId: "sc-f4n4bf-0"
})(["", " &::before{content:'';display:block;height:0;", "}"], function (p) {
  return p.withDividers && "\n\t\toverflow: hidden;\n\t";
}, function (p) {
  return p.withDividers ? dividerCompensationProp : spacingCompensationProp;
});
function getRoles(as) {
  switch (as) {
    case 'list':
      return ['ul', 'li'];
    case 'ordered-list':
      return ['ol', 'li'];
    default:
      return ['div', 'div'];
  }
}
function getHiddenChildProps(child) {
  if (_typeof(child) === 'object' && child.type === Hidden) {
    return child.props;
  } else return null;
}
function Stack(_ref) {
  var children = _ref.children,
    spacing = _ref.spacing,
    withDividers = _ref.withDividers,
    breakpoints = _ref.breakpoints,
    _ref$as = _ref.as,
    as = _ref$as === void 0 ? 'default' : _ref$as,
    otherProps = _objectWithoutProperties(_ref, _excluded);
  var _getRoles = getRoles(as),
    _getRoles2 = _slicedToArray(_getRoles, 2),
    wrapperAs = _getRoles2[0],
    itemAs = _getRoles2[1];
  return /*#__PURE__*/React.createElement(Wrapper, _extends({}, otherProps, {
    withDividers: withDividers,
    compensateSpacing: spacing,
    as: wrapperAs,
    breakpoints: breakpoints
  }), React.Children.map(children, function (child) {
    if (!child) return null;
    var hiddenChildProps = getHiddenChildProps(child);
    if (hiddenChildProps && (hiddenChildProps.above || hiddenChildProps.below)) {
      if (hiddenChildProps !== null && hiddenChildProps !== void 0 && hiddenChildProps.as) {
        // eslint-disable-next-line no-console
        console.warn('The prop `as` is not valid on a Hidden component. The element used will be determined by the Stack.');
      }
      return /*#__PURE__*/React.createElement(Hidden, {
        as: itemAs,
        above: hiddenChildProps.above,
        below: hiddenChildProps.below,
        pt: spacing,
        breakpoints: breakpoints
      }, withDividers && /*#__PURE__*/React.createElement(Divider, {
        mb: spacing
      }), hiddenChildProps.children);
    }
    return /*#__PURE__*/React.createElement(Box, {
      as: itemAs,
      pt: spacing,
      breakpoints: breakpoints
    }, withDividers && /*#__PURE__*/React.createElement(Divider, {
      mb: spacing
    }), child);
  }));
}
var StandaloneStackContext = /*#__PURE__*/createContext(undefined);
function useStandaloneStackContext() {
  var context = useContext(StandaloneStackContext);
  if (context === undefined) {
    throw Error('StackItem must be used inside of a StackWrapper component');
  }
  return context;
}
var StackWrapper = /*#__PURE__*/forwardRef(function (_ref2, ref) {
  var children = _ref2.children,
    _ref2$as = _ref2.as,
    as = _ref2$as === void 0 ? 'default' : _ref2$as,
    spacing = _ref2.spacing,
    withDividers = _ref2.withDividers,
    breakpoints = _ref2.breakpoints,
    otherProps = _objectWithoutProperties(_ref2, _excluded2);
  var _getRoles3 = getRoles(as),
    _getRoles4 = _slicedToArray(_getRoles3, 2),
    wrapperAs = _getRoles4[0],
    itemAs = _getRoles4[1];
  return /*#__PURE__*/React.createElement(Wrapper, _extends({}, otherProps, {
    ref: ref,
    as: wrapperAs,
    compensateSpacing: spacing,
    withDividers: withDividers,
    breakpoints: breakpoints
  }), /*#__PURE__*/React.createElement(StandaloneStackContext.Provider, {
    value: {
      as: itemAs,
      spacing: spacing,
      breakpoints: breakpoints,
      withDividers: withDividers
    }
  }, children));
});
StackWrapper.displayName = 'StackWrapper';
var StackItem = /*#__PURE__*/forwardRef(function (_ref3, ref) {
  var children = _ref3.children,
    hiddenAbove = _ref3.hiddenAbove,
    hiddenBelow = _ref3.hiddenBelow,
    allowUnknownProps = _ref3.allowUnknownProps,
    otherProps = _objectWithoutProperties(_ref3, _excluded3);
  var _useStandaloneStackCo = useStandaloneStackContext(),
    as = _useStandaloneStackCo.as,
    spacing = _useStandaloneStackCo.spacing,
    withDividers = _useStandaloneStackCo.withDividers,
    breakpoints = _useStandaloneStackCo.breakpoints;
  if (hiddenBelow || hiddenAbove) {
    return /*#__PURE__*/React.createElement(Hidden, _extends({}, allowUnknownProps ? otherProps : undefined, {
      ref: ref,
      as: as,
      pt: spacing,
      below: hiddenBelow,
      above: hiddenAbove,
      breakpoints: breakpoints
    }), withDividers && /*#__PURE__*/React.createElement(Divider, {
      mb: spacing
    }), children);
  }
  return /*#__PURE__*/React.createElement(Box, _extends({}, allowUnknownProps ? otherProps : undefined, {
    ref: ref,
    as: as,
    pt: spacing,
    breakpoints: breakpoints
  }), withDividers && /*#__PURE__*/React.createElement(Divider, {
    mb: spacing
  }), children);
});
StackItem.displayName = 'StackItem';
export { StackWrapper, StackItem };
export default Stack;